// The sales page's measure and period, apart from the screen so they can be
// tested. Every amount arrives from `GET /api/reports/sales` in four measures;
// this picks the one the reader chose.

import type { SalesAmounts } from "@crm/shared";
import {
  addDays,
  addMonths,
  calendarMonthOf,
  formatIsoMonth,
  parseCalendarDate,
  type CalendarMonth,
} from "../../format/calendarDate.js";

/** Which of the four amounts a chart reads. */
export type Basis = "gross" | "our_share";

export interface Measure {
  readonly basis: Basis;
  /** Multiplied by each deal's win probability. */
  readonly weighted: boolean;
}

export const DEFAULT_MEASURE: Measure = { basis: "gross", weighted: true };

export function amountOf(amounts: SalesAmounts, measure: Measure): number {
  if (measure.basis === "gross")
    return measure.weighted ? amounts.weighted_minor : amounts.gross_minor;
  return measure.weighted ? amounts.weighted_our_share_minor : amounts.our_share_minor;
}

// ---------------------------------------------------------------- window

/** The days the page covers, inclusive. `to` null means up to today. */
export interface Period {
  readonly from: string;
  readonly to: string | null;
}

/** The first day of the month `today` is in. */
export function monthStart(today: string): string {
  return `${today.slice(0, 7)}-01`;
}

export function thisMonth(today: string): Period {
  return { from: monthStart(today), to: null };
}

/** The earliest day "All" asks for: HubSpot's first stage is mid-2024. */
export const ALL_HISTORY_FROM = "2024-01-01";

export interface PeriodShortcut {
  readonly key: string;
  readonly label: string;
  readonly period: Period;
}

/** One-click periods, each ending today or at the end of a whole month. */
export function periodShortcuts(today: string): readonly PeriodShortcut[] {
  const month = calendarMonthOf(today) ?? { year: 2026, month: 1 };
  const firstOf = (m: CalendarMonth) => `${formatIsoMonth(m)}-01`;
  const lastOf = (m: CalendarMonth) => addDays(firstOf(addMonths(m, 1)), -1);
  const lastMonth = addMonths(month, -1);
  const quarterStart: CalendarMonth = {
    year: month.year,
    month: month.month - ((month.month - 1) % 3),
  };
  return [
    { key: "this-month", label: "This month", period: thisMonth(today) },
    {
      key: "last-month",
      label: "Last month",
      period: { from: firstOf(lastMonth), to: lastOf(lastMonth) },
    },
    {
      key: "this-quarter",
      label: "This quarter",
      period: { from: firstOf(quarterStart), to: null },
    },
    { key: "this-year", label: "This year", period: { from: `${month.year}-01-01`, to: null } },
    {
      key: "last-12",
      label: "Last 12 months",
      period: { from: firstOf(addMonths(month, -11)), to: null },
    },
    { key: "all", label: "All time", period: { from: ALL_HISTORY_FROM, to: null } },
  ];
}

/** The last day a period covers. */
export function periodEnd(period: Period, today: string): string {
  return period.to ?? today;
}

/** Weeks up to about a quarter, months beyond: a chart of a handful to a few dozen bars. */
export function bucketFor(period: Period, today: string): "week" | "month" {
  const from = parseCalendarDate(period.from);
  const to = parseCalendarDate(periodEnd(period, today));
  if (from === null || to === null) return "month";
  const days =
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) /
    86_400_000;
  return days <= 92 ? "week" : "month";
}

export function samePeriod(a: Period, b: Period): boolean {
  return a.from === b.from && a.to === b.to;
}

/** "+12%" / "−4%", for a figure set against where it started; null when it started at nothing. */
export function changeText(now: number, start: number): string | null {
  if (start === 0) return null;
  const rounded = Math.round(((now - start) / Math.abs(start)) * 100);
  return `${rounded >= 0 ? "+" : "−"}${Math.abs(rounded)}%`;
}
